import { useCallback, useState, type MutableRefObject } from 'react'
import { createPortal } from 'react-dom'
import type L from 'leaflet'
import { toCanvas } from 'html-to-image'
import { Printer, FileDown, X } from 'lucide-react'
import { GRUNDKARTEN, type GrundkarteWahl } from './kartenEbenen'

/*
 * Drucken / PDF der taktischen Karte (Führung und Admin):
 *  1. Dialog: Hintergrund und Ebenen wählen (vorbelegt mit dem, was gerade sichtbar ist).
 *  2. Die Seite stellt die Auswahl vorübergehend ein (`anwenden`, wird nirgends gespeichert), wartet, bis die Kacheln
 *     geladen sind, und das Kartenfenster wird als Bild aufgenommen (html-to-image; die Kachelserver senden CORS-Header).
 *  3. Daraus entsteht eine A4-Querformat-Seite (Kopf, Karte, Maßstab, Nordpfeil, Legende) – als Browser-Druck
 *     oder als PDF (einfaches Ein-Seiten-PDF mit JPEG, ohne Zusatzbibliothek).
 * Gedruckt wird der aktuell sichtbare Kartenausschnitt. Dialog und Bedienelemente liegen außerhalb des Kartenfensters
 * und kommen daher nicht ins Bild.
 */

export interface DruckEbene {
  key: string
  label: string
  an: boolean
  /** eingerückt als Unterebene der davor stehenden Ebene */
  unter?: boolean
  /** Farbpunkt (z. B. Geozonen) */
  farbe?: string
}

export interface DruckAuswahl {
  grundkarte: GrundkarteWahl
  ebenen: Record<string, boolean>
}

export interface DruckKopf {
  titel: string
  nummer?: string | null
  ort?: string | null
}

export interface LegendenEintrag { svg: string; label: string }

const SEITE_W = 2339, SEITE_H = 1654   // A4 quer bei 200 dpi
const RAND = 60

/* ------------------------------------------------------------- Aufnahme */

const warte = (ms: number) => new Promise<void>(r => setTimeout(r, ms))

/** Wartet, bis die Karte nach einer Ebenenänderung fertig gezeichnet ist (Kacheln geladen). */
async function warteAufKarte(container: HTMLElement): Promise<void> {
  await new Promise<void>(r => requestAnimationFrame(() => requestAnimationFrame(() => r())))
  await warte(300)
  const ende = Date.now() + 15000
  while (Date.now() < ende) {
    const offen = Array.from(container.querySelectorAll<HTMLImageElement>('img.leaflet-tile')).some(i => !i.complete || !i.classList.contains('leaflet-tile-loaded'))
    if (!offen) break
    await warte(150)
  }
  await warte(400)
}

/** Prüft je Kachelserver, ob die Kacheln mit CORS lesbar sind – sonst bliebe die Karte im Bild leer. */
async function pruefeKachelZugriff(container: HTMLElement): Promise<void> {
  const proHost = new Map<string, string>()
  container.querySelectorAll<HTMLImageElement>('img.leaflet-tile').forEach(i => {
    try { const h = new URL(i.src).host; if (!proHost.has(h)) proHost.set(h.replace(/^[a-c]\./, ''), i.src) } catch { /* ignorieren */ }
  })
  for (const [host, src] of Array.from(proHost.entries())) {
    try {
      const r = await fetch(src, { mode: 'cors' })
      if (!r.ok) throw new Error(String(r.status))
    } catch {
      throw new Error(`Die Kacheln von ${host} lassen sich nicht in ein Bild übernehmen (Server erlaubt keinen Zugriff). Bitte diese Ebene bzw. den Hintergrund abwählen.`)
    }
  }
}

async function ladeBild(src: string): Promise<HTMLImageElement> {
  return new Promise((ok, fehl) => {
    const i = new Image()
    i.onload = () => ok(i)
    i.onerror = () => fehl(new Error('Bild konnte nicht geladen werden'))
    i.src = src
  })
}
const svgBild = (svg: string) => ladeBild('data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg))

/* --------------------------------------------------------- Seite zeichnen */

const SCHOEN = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000]

function formatDatum(d: Date): string {
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) + ' Uhr'
}

function zeichneSeite(opts: {
  karte: HTMLCanvasElement
  /** CSS-Pixel des Kartenfensters (für den Maßstab) */
  kartePx: { w: number; h: number }
  meterProPixel: number
  kopf: DruckKopf
  legende: Array<{ bild: HTMLImageElement; label: string }>
  attribution: string
  jetzt: Date
}): HTMLCanvasElement {
  const c = document.createElement('canvas')
  c.width = SEITE_W; c.height = SEITE_H
  const g = c.getContext('2d')!
  g.fillStyle = '#ffffff'; g.fillRect(0, 0, SEITE_W, SEITE_H)
  const font = 'Arial, Helvetica, sans-serif'

  // Kopf
  g.fillStyle = '#111827'; g.textBaseline = 'alphabetic'
  g.font = `bold 56px ${font}`
  g.fillText(fitText(g, opts.kopf.titel || 'Einsatz', SEITE_W - 2 * RAND - 620), RAND, RAND + 52)
  g.font = `32px ${font}`; g.fillStyle = '#374151'
  const unter = [opts.kopf.nummer ? `Einsatznummer ${opts.kopf.nummer}` : '', opts.kopf.ort ?? ''].filter(Boolean).join('  ·  ')
  g.fillText(fitText(g, unter || 'Taktische Karte', SEITE_W - 2 * RAND - 620), RAND, RAND + 100)
  g.textAlign = 'right'
  g.font = `bold 34px ${font}`; g.fillStyle = '#111827'
  g.fillText('Taktische Karte', SEITE_W - RAND, RAND + 52)
  g.font = `28px ${font}`; g.fillStyle = '#374151'
  g.fillText(`Gedruckt am ${formatDatum(opts.jetzt)}`, SEITE_W - RAND, RAND + 100)
  g.textAlign = 'left'
  g.strokeStyle = '#111827'; g.lineWidth = 3
  g.beginPath(); g.moveTo(RAND, RAND + 122); g.lineTo(SEITE_W - RAND, RAND + 122); g.stroke()

  // Fußbereich: Legende (bis zu 2 Zeilen) + Quelle
  const legendeH = opts.legende.length ? 150 : 60
  const kartenTop = RAND + 142
  const kartenBottom = SEITE_H - RAND - legendeH
  const frei = { w: SEITE_W - 2 * RAND, h: kartenBottom - kartenTop }
  const s = Math.min(frei.w / opts.kartePx.w, frei.h / opts.kartePx.h)
  const dw = Math.round(opts.kartePx.w * s), dh = Math.round(opts.kartePx.h * s)
  const dx = RAND + Math.round((frei.w - dw) / 2), dy = kartenTop
  g.drawImage(opts.karte, dx, dy, dw, dh)
  g.strokeStyle = '#111827'; g.lineWidth = 3; g.strokeRect(dx, dy, dw, dh)

  // Maßstabsbalken (unten links im Bild)
  const mProSeitenPx = opts.meterProPixel / s
  const maxM = dw * 0.25 * mProSeitenPx
  const nice = [...SCHOEN].reverse().find(m => m <= maxM) ?? SCHOEN[0]
  const barW = nice / mProSeitenPx
  const bx = dx + 28, by = dy + dh - 40
  g.fillStyle = 'rgba(255,255,255,0.85)'
  g.fillRect(bx - 14, by - 54, Math.max(barW, 90) + 28, 84)
  g.strokeStyle = '#111827'; g.lineWidth = 5
  g.beginPath(); g.moveTo(bx, by - 12); g.lineTo(bx, by); g.lineTo(bx + barW, by); g.lineTo(bx + barW, by - 12); g.stroke()
  g.fillStyle = '#111827'; g.font = `bold 28px ${font}`
  g.fillText(nice >= 1000 ? `${nice / 1000} km` : `${nice} m`, bx, by - 20)

  // Nordpfeil (oben rechts im Bild)
  const nx = dx + dw - 60, ny = dy + 40
  g.fillStyle = 'rgba(255,255,255,0.85)'; g.fillRect(nx - 40, ny - 20, 80, 130)
  g.fillStyle = '#111827'
  g.beginPath(); g.moveTo(nx, ny); g.lineTo(nx + 22, ny + 70); g.lineTo(nx, ny + 54); g.lineTo(nx - 22, ny + 70); g.closePath(); g.fill()
  g.textAlign = 'center'; g.font = `bold 34px ${font}`
  g.fillText('N', nx, ny + 106); g.textAlign = 'left'

  // Legende
  let lx = RAND, ly = kartenBottom + 24
  g.font = `26px ${font}`
  if (opts.legende.length) {
    g.fillStyle = '#111827'; g.font = `bold 26px ${font}`
    g.fillText('Legende', lx, ly + 22)
    lx += 150
    g.font = `26px ${font}`
    for (const e of opts.legende) {
      const w = 52 + 10 + g.measureText(e.label).width + 40
      if (lx + w > SEITE_W - RAND) { lx = RAND + 150; ly += 54 }
      if (ly > SEITE_H - RAND - 70) break
      g.drawImage(e.bild, lx, ly - 2, 52, 40)
      g.fillStyle = '#111827'
      g.fillText(e.label, lx + 62, ly + 28)
      lx += w
    }
  }
  g.fillStyle = '#6b7280'; g.font = `22px ${font}`; g.textAlign = 'right'
  g.fillText(fitText(g, opts.attribution, SEITE_W - 2 * RAND), SEITE_W - RAND, SEITE_H - RAND + 10)
  g.textAlign = 'left'
  return c
}

function fitText(g: CanvasRenderingContext2D, text: string, maxW: number): string {
  if (g.measureText(text).width <= maxW) return text
  let t = text
  while (t.length > 1 && g.measureText(t + '…').width > maxW) t = t.slice(0, -1)
  return t + '…'
}

/* --------------------------------------------------- Ausgabe: PDF / Druck */

function jpegBytes(c: HTMLCanvasElement): Uint8Array {
  const b64 = c.toDataURL('image/jpeg', 0.92).split(',')[1]
  const bin = atob(b64)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

/** Einfaches Ein-Seiten-PDF (A4 quer) mit dem Seitenbild – ohne Zusatzbibliothek. */
export function seiteAlsPdf(c: HTMLCanvasElement, titel: string): Blob {
  const enc = new TextEncoder()
  const teile: Uint8Array[] = []
  const offsets: number[] = []
  let pos = 0
  const add = (d: Uint8Array | string) => { const b = typeof d === 'string' ? enc.encode(d) : d; teile.push(b); pos += b.length }
  const obj = (n: number, inhalt: string) => { offsets[n] = pos; add(`${n} 0 obj\n${inhalt}\nendobj\n`) }
  const jpg = jpegBytes(c)
  const W = 841.89, H = 595.28
  const titelPdf = '(' + titel.replace(/[^\x20-\x7e]/g, '?').replace(/[()\\]/g, '') + ')'
  add('%PDF-1.4\n')
  obj(1, '<< /Type /Catalog /Pages 2 0 R >>')
  obj(2, '<< /Type /Pages /Kids [3 0 R] /Count 1 >>')
  obj(3, `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${W} ${H}] /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>`)
  offsets[4] = pos
  add(`4 0 obj\n<< /Type /XObject /Subtype /Image /Width ${c.width} /Height ${c.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpg.length} >>\nstream\n`)
  add(jpg); add('\nendstream\nendobj\n')
  const inhalt = `q ${W} 0 0 ${H} 0 0 cm /Im0 Do Q`
  obj(5, `<< /Length ${inhalt.length} >>\nstream\n${inhalt}\nendstream`)
  obj(6, `<< /Title ${titelPdf} /Producer (Pilot-Hub) >>`)
  const xref = pos
  add(`xref\n0 7\n0000000000 65535 f \n`)
  for (let n = 1; n <= 6; n++) add(`${String(offsets[n]).padStart(10, '0')} 00000 n \n`)
  add(`trailer\n<< /Size 7 /Root 1 0 R /Info 6 0 R >>\nstartxref\n${xref}\n%%EOF\n`)
  return new Blob(teile as BlobPart[], { type: 'application/pdf' })
}

export function dateiname(s: string): string {
  return s.replace(/[\\/:*?"<>|\x00-\x1f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 100) || 'Taktische Karte'
}

function herunterladen(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = name
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10000)
}

/** Öffnet den Browser-Druckdialog für die Seite (A4 quer, ohne Ränder). */
function seiteDrucken(c: HTMLCanvasElement, titel: string): Promise<void> {
  return new Promise((ok, fehl) => {
    const iframe = document.createElement('iframe')
    iframe.setAttribute('aria-hidden', 'true')
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden'
    document.body.appendChild(iframe)
    const doc = iframe.contentDocument
    const win = iframe.contentWindow
    if (!doc || !win) { iframe.remove(); fehl(new Error('Druckvorschau nicht verfügbar')); return }
    const weg = () => setTimeout(() => iframe.remove(), 500)
    doc.open()
    doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${titel.replace(/[<>&]/g, '')}</title>
<style>@page{size:A4 landscape;margin:0}html,body{margin:0;padding:0;background:#fff}img{display:block;width:297mm;height:210mm}</style></head>
<body><img id="s" alt="Taktische Karte"></body></html>`)
    doc.close()
    const img = doc.getElementById('s') as HTMLImageElement
    img.onload = () => {
      try {
        win.addEventListener('afterprint', weg)
        win.focus(); win.print()
        setTimeout(weg, 120000)
        ok()
      } catch (e) { weg(); fehl(e as Error) }
    }
    img.onerror = () => { weg(); fehl(new Error('Druckbild konnte nicht erzeugt werden')) }
    img.src = c.toDataURL('image/jpeg', 0.92)
  })
}

/* ------------------------------------------------------ Komponente/Dialog */

export function KartenDruck({ mapRef, kopf, grundkarte, ebenen, anwenden, legende, attribution, nachlaufMs, kompakt = false, className = '' }: {
  mapRef: MutableRefObject<L.Map | null>
  kopf: DruckKopf
  /** aktuell eingestellter Hintergrund */
  grundkarte: GrundkarteWahl
  /** alle verfügbaren Ebenen mit ihrem aktuellen Zustand */
  ebenen: DruckEbene[]
  /** stellt die Auswahl vorübergehend ein (nicht speichern!); `null` = zurück zum eigentlichen Zustand */
  anwenden: (a: DruckAuswahl | null) => void
  /** Legendeneinträge zur gewählten Auswahl */
  legende?: (a: DruckAuswahl) => LegendenEintrag[]
  /** Quellenangabe für den Seitenfuß (ohne HTML) */
  attribution?: (a: DruckAuswahl) => string
  /** zusätzliche Wartezeit (ms) nach dem Umschalten, z. B. wenn Ebenen erst Daten nachladen */
  nachlaufMs?: (a: DruckAuswahl) => number
  kompakt?: boolean
  className?: string
}) {
  const [offen, setOffen] = useState(false)
  const [auswahl, setAuswahl] = useState<DruckAuswahl>({ grundkarte, ebenen: {} })
  const [laeuft, setLaeuft] = useState<'druck' | 'pdf' | null>(null)
  const [fehler, setFehler] = useState('')

  const oeffnen = async () => {
    // Im nativen Vollbild wäre der Dialog (außerhalb der Bühne) unsichtbar
    const d = document as any
    if (d.fullscreenElement || d.webkitFullscreenElement) { try { await (d.exitFullscreen?.() ?? d.webkitExitFullscreen?.()) } catch { /* ignorieren */ } }
    setAuswahl({ grundkarte, ebenen: ebenen.reduce<Record<string, boolean>>((o, e) => { o[e.key] = e.an; return o }, {}) })
    setFehler(''); setOffen(true)
  }

  const ausfuehren = useCallback(async (modus: 'druck' | 'pdf') => {
    const map = mapRef.current
    if (!map) { setFehler('Die Karte ist noch nicht bereit.'); return }
    const container = map.getContainer()
    setLaeuft(modus); setFehler('')
    try {
      anwenden(auswahl)
      await warteAufKarte(container)
      const extra = nachlaufMs?.(auswahl) ?? 0
      if (extra) await warte(extra)
      if (auswahl.grundkarte !== 'keine' || container.querySelector('img.leaflet-tile')) await pruefeKachelZugriff(container)
      const w = container.clientWidth, h = container.clientHeight
      const faktor = Math.min(3, Math.max(1, (SEITE_W - 2 * RAND) / w))
      const bild = await toCanvas(container, {
        pixelRatio: faktor, skipFonts: true, cacheBust: false,
        backgroundColor: '#ffffff',
        filter: n => !(n instanceof HTMLElement && (n.classList.contains('leaflet-control-container'))),
      })
      const zentrum = map.getCenter()
      const mpp = 156543.03392 * Math.cos(zentrum.lat * Math.PI / 180) / Math.pow(2, map.getZoom())
      const leg = await Promise.all((legende?.(auswahl) ?? []).map(async e => ({ bild: await svgBild(e.svg), label: e.label })))
      const attr = attribution?.(auswahl) ?? ''
      const jetzt = new Date()
      const seite = zeichneSeite({ karte: bild, kartePx: { w, h }, meterProPixel: mpp, kopf, legende: leg, attribution: attr, jetzt })
      const titel = `Taktische Karte ${[kopf.nummer, kopf.titel].filter(Boolean).join(' ')}`
      if (modus === 'pdf') herunterladen(seiteAlsPdf(seite, titel), dateiname(titel) + '.pdf')
      else await seiteDrucken(seite, titel)
      setOffen(false)
    } catch (e: any) {
      setFehler(e?.message ? String(e.message) : 'Die Karte konnte nicht erzeugt werden.')
    } finally {
      anwenden(null)
      setLaeuft(null)
    }
  }, [mapRef, auswahl, anwenden, legende, attribution, nachlaufMs, kopf])

  const schalten = (key: string, an: boolean) => setAuswahl(a => ({ ...a, ebenen: { ...a.ebenen, [key]: an } }))
  const knopfCls = kompakt
    ? 'inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-dark-border bg-dark-bg-secondary/95 px-2.5 text-xs font-semibold text-dark-text shadow-2xl hover:bg-dark-bg-tertiary'
    : 'inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-dark-border bg-dark-bg-secondary px-3 text-sm font-semibold text-dark-text hover:bg-dark-bg-tertiary'

  return (
    <>
      <button type="button" onClick={oeffnen} data-testid="karte-druck-knopf" className={`${knopfCls} ${className}`} title="Taktische Karte drucken oder als PDF speichern">
        <Printer className="h-4 w-4" /> Drucken / PDF
      </button>
      {offen && createPortal(
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 p-3" role="dialog" aria-modal="true" aria-label="Taktische Karte drucken" data-testid="karte-druck-dialog">
          <div className="flex max-h-[92vh] w-full max-w-lg flex-col rounded-2xl border border-dark-border bg-dark-bg-secondary shadow-2xl">
            <div className="flex items-center justify-between border-b border-dark-border px-4 py-3">
              <h2 className="text-base font-bold text-dark-text">Taktische Karte drucken</h2>
              <button type="button" onClick={() => !laeuft && setOffen(false)} aria-label="Schließen" disabled={!!laeuft}
                className="flex h-10 w-10 items-center justify-center rounded-lg text-dark-text-muted hover:bg-dark-bg-tertiary"><X className="h-4 w-4" /></button>
            </div>
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-3">
              <p className="text-xs text-dark-text-subtle">Gedruckt wird der aktuell sichtbare Kartenausschnitt (A4 quer) mit den hier gewählten Ebenen. Die Karte selbst bleibt unverändert.</p>
              <div>
                <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-dark-text-subtle">Hintergrund</div>
                <div className="grid grid-cols-2 gap-1">
                  {([...GRUNDKARTEN.map(g => ({ key: g.key as GrundkarteWahl, label: g.label })), { key: 'keine' as GrundkarteWahl, label: 'Ohne (weiß)' }]).map(g => (
                    <label key={g.key} className="flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg px-2 text-sm text-dark-text hover:bg-dark-bg-tertiary">
                      <input type="radio" name="druck-grundkarte" checked={auswahl.grundkarte === g.key} disabled={!!laeuft}
                        onChange={() => setAuswahl(a => ({ ...a, grundkarte: g.key }))} className="h-5 w-5 accent-primary-500" data-testid={`druck-grund-${g.key}`} />
                      {g.label}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-dark-text-subtle">Ebenen</div>
                <div className="space-y-0.5">
                  {ebenen.map(e => (
                    <label key={e.key} className={`flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg px-2 text-sm text-dark-text hover:bg-dark-bg-tertiary ${e.unter ? 'ml-6' : ''}`}>
                      <input type="checkbox" checked={!!auswahl.ebenen[e.key]} disabled={!!laeuft}
                        onChange={ev => schalten(e.key, ev.target.checked)} className="h-5 w-5 rounded accent-primary-500" data-testid={`druck-ebene-${e.key}`} />
                      {e.farbe && <span className="h-3 w-3 shrink-0 rounded-sm" style={{ background: e.farbe }} />}
                      <span className="flex-1">{e.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              {fehler && <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200" role="alert" data-testid="druck-fehler">{fehler}</p>}
              {laeuft && <p className="text-xs text-dark-text-muted" role="status">Karte wird aufgenommen … bitte kurz warten.</p>}
            </div>
            <div className="flex flex-wrap justify-end gap-2 border-t border-dark-border px-4 py-3">
              <button type="button" onClick={() => setOffen(false)} disabled={!!laeuft}
                className="min-h-[44px] rounded-xl border border-dark-border px-4 text-sm font-semibold text-dark-text hover:bg-dark-bg-tertiary disabled:opacity-50">Abbrechen</button>
              <button type="button" onClick={() => void ausfuehren('pdf')} disabled={!!laeuft} data-testid="druck-pdf"
                className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-dark-border px-4 text-sm font-semibold text-dark-text hover:bg-dark-bg-tertiary disabled:opacity-50">
                <FileDown className="h-4 w-4" /> Als PDF speichern
              </button>
              <button type="button" onClick={() => void ausfuehren('druck')} disabled={!!laeuft} data-testid="druck-drucken"
                className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-primary-500 px-4 text-sm font-semibold text-white hover:bg-primary-600 disabled:opacity-50">
                <Printer className="h-4 w-4" /> Drucken
              </button>
            </div>
          </div>
        </div>,
        document.body)}
    </>
  )
}
